import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowUpRight,
  Check,
  X,
  Phone,
  Award,
  ShieldCheck,
  Sparkles,
  Heart,
  Wrench,
  ThumbsUp,
  Plus,
  Minus,
  Search,
  Droplets,
  Brush,
  SprayCan,
  Star,
} from "lucide-react";
import SEO from "@/components/SEO";
import BeforeAfter, { NettoyageBeforeAfter } from "@/components/BeforeAfter";
import QuoteForm from "@/components/QuoteForm";
import { COMPANY, TESTIMONIALS } from "@/data";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] },
  }),
};

/* ============ DATA ============ */

const PILLARS = [
  { icon: SprayCan, title: "Produits professionnels", text: "Koch Chemie, Gyeon, CarPro, les meilleures marques du marché." },
  { icon: Wrench, title: "Matériel spécialisé", text: "Polisseuses, injection-extraction, éclairage LED inspection." },
  { icon: Search, title: "Nettoyage minutieux", text: "Chaque recoin est traité. Aucun raccourci, aucune approximation." },
  { icon: Heart, title: "Respect des matériaux", text: "Produits adaptés à chaque surface, cuir, tissu, vernis, plastique." },
  { icon: Award, title: "Résultats premium", text: "Un standard concours pour tous les véhicules confiés." },
  { icon: ThumbsUp, title: "Satisfaction client", text: "5,0 / 35 avis Google. Aucun compromis sur la qualité." },
];

const FORMULAS_SETS = {
  complet: {
    label: "Complet",
    sublabel: "Intérieur + Extérieur",
    formulas: [
      {
        id: "classique",
        name: "Classique",
        summary: "Un nettoyage soigné pour l'entretien régulier de votre véhicule.",
        duration: "2h à 2h30",
        price: "79",
        highlighted: false,
        includes: [
          "Lavage extérieur shampoing",
          "Séchage à la main",
          "Nettoyage jantes et pneus",
          "Aspiration de l'habitacle et coffre",
          "Vitres intérieures et extérieures",
          "Nettoyage et dépoussiérage intérieur",
        ],
      },
      {
        id: "premium",
        name: "Premium",
        summary: "L'équilibre parfait entre nettoyage approfondi et rapport qualité-prix.",
        duration: "3h à 4h30",
        price: "169",
        highlighted: false,
        includes: [
          "Formule Classique +",
          "Prélavage carrosserie",
          "Nettoyage des jantes détaillées au pinceau",
          "Shampoing des sièges et des tapis",
          "Nettoyage du volant",
          "Nettoyage contour des portes",
          "Traitement des plastiques intérieur",
          "Dressing pneus",
          "Désodorisant intérieur",
        ],
      },
      {
        id: "ultimate",
        name: "Ultimate",
        summary: "L'expérience VistaWash au niveau concours, protection et brillance longue durée.",
        duration: "5h à 6h30",
        price: "229",
        highlighted: true,
        ribbon: "Coup de cœur clients",
        includes: [
          "Formule Premium +",
          "Passages de roues",
          "Décontamination chimique",
          "Traitement des plastiques extérieur",
          "Cire de protection hydrophobe carrosserie et jantes 3 mois",
          "Séchage au souffleur pour une finition nette",
        ],
      },
    ],
  },

  interieur: {
    label: "Intérieur",
    sublabel: "Habitacle uniquement",
    formulas: [
      {
        id: "interieur-complet",
        name: "Intérieur Complet",
        summary: "Un nettoyage en profondeur, sans compromis. Chaque surface de l'habitacle est traitée avec les produits adaptés.",
        duration: "3h à 4h30",
        price: "129",
        highlighted: true,
        ribbon: "Formule signature",
        includes: [
          "Aspiration approfondie habitacle et coffre",
          "Nettoyage des plastiques, tableau de bord, console et portes",
          "Nettoyage en profondeur des tapis et moquettes",
          "Shampoing / injecteur-extracteur des tissus (si nécessaire)",
          "Nettoyage adapté des sièges cuir",
          "Vitres intérieures, détails et finitions",
          "Désodorisation habitacle",
        ],
      },
    ],
  },

  exterieur: {
    label: "Extérieur",
    sublabel: "Carrosserie uniquement",
    formulas: [
      {
        id: "exterieur-complet",
        name: "Extérieur Complet",
        summary: "Bien plus qu'un lavage. Un protocole détaillé pour redonner à la carrosserie sa profondeur et sa protection.",
        duration: "2h30 à 3h30",
        price: "99",
        highlighted: true,
        ribbon: "Formule signature",
        includes: [
          "Prélavage mousse active",
          "Lavage manuel deux seaux, gant microfibre",
          "Jantes, pneus et passages de roues",
          "Détails emblèmes et zones difficiles",
          "Décontamination chimique ferreuse + goudron (si nécessaire)",
          "Séchage microfibre / souffleur",
          "Vitres extérieures",
          "Dressing pneus",
          "Protection hydrophobe carrosserie",
        ],
      },
    ],
  },
};

const SERVICE_TYPES = ["complet", "interieur", "exterieur"];

const BENEFITS = [
  {
    icon: Sparkles,
    title: "Une voiture comme neuve",
    text: "Un résultat digne d'une sortie de concession. Vos passagers vous demanderont si vous avez changé de voiture.",
  },
  {
    icon: Heart,
    title: "Une fierté retrouvée",
    text: "Retrouvez le plaisir de conduire une voiture impeccable, habitacle net, sièges frais, odeur premium.",
  },
  {
    icon: ShieldCheck,
    title: "Une protection longue durée",
    text: "Cire hydrophobe 3 mois (formule Ultimate) : l'eau perle, la saleté n'adhère plus, l'entretien devient un plaisir.",
  },
  {
    icon: Award,
    title: "Plus de valeur à la revente",
    text: "Un intérieur soigné et une carrosserie protégée peuvent augmenter la valeur de revente jusqu'à +15%.",
  },
  {
    icon: ThumbsUp,
    title: "Zéro compromis",
    text: "Produits pros (Koch Chemie, Gyeon, CarPro), microfibres neuves, lavage main. On soigne votre voiture comme la nôtre.",
  },
  {
    icon: Wrench,
    title: "Un service sans tracas",
    text: "Devis clair, atelier à Châtelet, prise en charge sur rendez-vous. Vous déposez, vous récupérez, vous profitez.",
  },
];

const COMPARE = [
  {
    aspect: "Temps consacré",
    classic: "20 à 30 minutes",
    vistawash: "2 à 7 heures selon la formule",
  },
  {
    aspect: "Produits utilisés",
    classic: "Détergents standards, souvent agressifs",
    vistawash: "Koch Chemie, Gyeon, CarPro, pH neutres et adaptés",
  },
  {
    aspect: "Méthode",
    classic: "Brosses mécaniques, éponges recyclées",
    vistawash: "Lavage main 2 seaux, microfibres dédiées, aucun contact abrasif",
  },
  {
    aspect: "Résultat visuel",
    classic: "Propreté de surface, traces de séchage",
    vistawash: "Finition concours, brillance profonde, aucune trace",
  },
  {
    aspect: "Protection appliquée",
    classic: "Aucune ou temporaire (quelques jours)",
    vistawash: "Cire ou céramique, protection de 3 mois à 5 ans",
  },
  {
    aspect: "Durabilité",
    classic: "Se salit en quelques jours",
    vistawash: "Effet hydrophobe, plus facile à entretenir dans le temps",
  },
];

const ADDONS = [
  { label: "Shampoing sièges", price: "+50€" },
  { label: "Traitement cuirs nourrissant", price: "+60€" },
  { label: "Désinfection habitacle à la vapeur", price: "+50€" },
  { label: "Nettoyage spécial (vomi, souillures…)", price: "+50€" },
  { label: "Un peu de poils d'animaux", price: "+25€" },
  { label: "Beaucoup de poils d'animaux", price: "+50€" },
  { label: "Sable excessif habitacle", price: "+30€" },
  { label: "Traitement moisissure", price: "+60€" },
  { label: "Frais de déplacement au-delà de 20 km", price: "0,60 €/km" },
];

const FAQ = [
  {
    q: "Combien de temps dure la prestation ?",
    a: "Selon la formule choisie : 2h à 2h30 pour la Classique Complète, 3h à 4h30 pour la Premium Complète ou l'Intérieur Complet, 2h30 à 3h30 pour l'Extérieur Complet, et 5h à 6h30 pour l'Ultimate. Nous vous communiquons toujours un créneau précis à la réservation.",
  },
  {
    q: "Dois-je laisser la voiture toute la journée ?",
    a: "Oui, pour les formules Premium et Ultimate, il est recommandé de laisser le véhicule à notre atelier de Châtelet pour une demi-journée ou une journée complète. Pour la Classique, vous pouvez patienter sur place ou revenir en fin de prestation.",
  },
  {
    q: "Le nettoyage enlève-t-il les odeurs ?",
    a: "Oui. Les formules Premium et Ultimate incluent un désodorisant intérieur et un nettoyage en profondeur qui neutralisent efficacement les mauvaises odeurs. Un parfum d'habitacle finalise la prestation.",
  },
  {
    q: "Les tarifs sont-ils identiques pour tous les véhicules ?",
    a: "Les prix affichés correspondent à une citadine. Un supplément est appliqué selon le gabarit : +10€ pour une berline ou un break, +20€ pour un SUV. Ces suppléments couvrent le temps et les produits supplémentaires nécessaires.",
  },
  {
    q: "Travaillez-vous sur les véhicules récents ?",
    a: "Absolument. Du véhicule neuf sortant de concession au youngtimer, en passant par les supercars et véhicules de collection, chaque voiture reçoit un traitement adapté à ses matériaux et à son état.",
  },
  {
    q: "Les sièges en cuir sont-ils traités ?",
    a: "Oui, dans les formules Premium et Ultimate. Nous utilisons des produits spécifiques pour cuir (nettoyants pH neutres + nourrissant) qui préservent la souplesse et la couleur d'origine.",
  },
  {
    q: "Quelle formule choisir ?",
    a: "Classique si votre véhicule est déjà relativement propre et que vous voulez un entretien régulier. Premium si vous voulez un nettoyage complet avec shampoing des sièges. Ultimate si vous voulez le meilleur : décontamination chimique, cire hydrophobe 3 mois et finition au souffleur.",
  },
];

const NettoyagePremium = () => (
  <>
    <SEO
      title="Nettoyage Automobile Premium Charleroi & Châtelet | Detailing VistaWash"
      description="Nettoyage voiture haut de gamme à Châtelet & Charleroi. Detailing intérieur et extérieur premium, formules Classique, Premium, Ultimate dès 79€. Devis gratuit."
    />
    <Hero />
    <PillarsSection />
    <FormulasSection />
    <NettoyageBeforeAfter />
    <BenefitsSection />
    <CompareSection />
    <ReviewsSection />
    <FaqSection />
    <FinalCTA />
  </>
);

/* ============ HERO ============ */
const Hero = () => (
  <section
    data-testid="nett-hero"
    className="relative min-h-[90vh] flex items-center pt-40 pb-24 grain bg-black"
  >
    <div className="absolute inset-0 z-0">
      <img
        src="https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=1920&q=90"
        alt="Porsche 911 detailing premium VistaWash Châtelet"
        className="w-full h-full object-cover hero-zoom"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/50" />
    </div>

    <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-12 w-full">
      <motion.div initial="hidden" animate="visible" variants={fadeUp} className="flex items-center gap-3 mb-6">
        <div className="w-12 h-px bg-[#D90000]" />
        <span className="text-[11px] uppercase tracking-[0.35em] text-[#D90000]">
          Detailing automobile premium · Charleroi & Châtelet
        </span>
      </motion.div>

      <motion.h1
        initial="hidden"
        animate="visible"
        variants={fadeUp}
        custom={1}
        className="font-impact text-5xl sm:text-6xl md:text-7xl lg:text-[96px] leading-[0.9] max-w-5xl"
      >
        REDONNEZ À VOTRE <br />
        VOITURE <span className="text-[#D90000]">L'ÉCLAT</span> <br />
        DU PREMIER JOUR.
      </motion.h1>

      <motion.p
        initial="hidden"
        animate="visible"
        variants={fadeUp}
        custom={2}
        className="text-white/75 text-base md:text-lg max-w-2xl mt-8 leading-relaxed"
      >
        Nettoyage automobile haut de gamme à Châtelet, intérieur, extérieur, décontamination et protection.
        Un travail de precision réalisé par des passionnés, au service des véhicules de la région de Charleroi.
      </motion.p>

      <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={3} className="flex flex-wrap gap-4 mt-10">
        <a
          href="#contact"
          data-testid="hero-quote-cta"
          className="inline-flex items-center gap-3 bg-[#D90000] hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-5 transition-all border border-[#D90000] hover:border-white"
        >
          Demander un devis gratuit <ArrowUpRight size={16} />
        </a>
        <a
          href="#avant-apres"
          data-testid="hero-results-cta"
          className="inline-flex items-center gap-3 border border-white/30 hover:border-white hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-5 transition-all"
        >
          Voir les résultats
        </a>
      </motion.div>

      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeUp}
        custom={4}
        className="mt-16 flex flex-wrap items-center gap-6 text-[11px] uppercase tracking-[0.22em] text-white/50"
      >
        <div className="flex items-center gap-2">
          <div className="flex gap-0.5 text-[#FBBC04]">
            {[...Array(5)].map((_, j) => (
              <Star key={j} size={14} fill="currentColor" stroke="none" />
            ))}
          </div>
          <span>5,0 · 35 avis Google</span>
        </div>
        <div className="flex items-center gap-2">
          <ShieldCheck size={14} className="text-[#D90000]" /> Garantie satisfaction
        </div>
        <div className="flex items-center gap-2">
          <Award size={14} className="text-[#D90000]" /> +300 véhicules traités
        </div>
      </motion.div>
    </div>
  </section>
);

/* ============ PILLARS ============ */
const PillarsSection = () => (
  <section data-testid="nett-pillars" className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a]">
    <div className="max-w-[1500px] mx-auto">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Pourquoi VistaWash</span>
        <h2 className="font-impact text-5xl md:text-7xl lg:text-[90px] leading-[0.9] mt-6">
          POURQUOI CHOISIR <span className="text-[#D90000]">VISTAWASH</span>
        </h2>
      </motion.div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {PILLARS.map((p, i) => {
          const Icon = p.icon;
          return (
            <motion.div
              key={p.title}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={i}
              data-testid={`pillar-${i}`}
              className="p-8 md:p-10 border border-white/10 bg-[#050505] hover:border-[#D90000]/50 transition-colors"
            >
              <Icon size={32} className="text-[#D90000] mb-6" />
              <h3 className="font-impact text-2xl md:text-3xl mb-3">{p.title}</h3>
              <p className="text-white/60 text-sm leading-relaxed">{p.text}</p>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ============ FORMULAS ============ */
const FormulasSection = () => {
  const [serviceType, setServiceType] = useState("complet");
  const current = FORMULAS_SETS[serviceType];
  const count = current.formulas.length;
  const gridClass =
    count === 1
      ? "grid grid-cols-1 max-w-2xl mx-auto"
      : count === 2
      ? "grid md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto"
      : "grid md:grid-cols-3 gap-6 lg:gap-8";
  return (
  <section id="formules" data-testid="nett-formules" className="py-24 md:py-32 px-6 md:px-12">
    <div className="max-w-[1500px] mx-auto">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-14">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Nos formules</span>
        <h2 className="font-impact text-5xl md:text-7xl lg:text-[100px] leading-[0.9] mt-6">
          NOS <span className="text-[#D90000]">FORMULES</span>
        </h2>
        <p className="text-white/60 mt-6 max-w-xl mx-auto">
          Choisissez d'abord le type de prestation, puis le niveau de finition qui vous correspond.
        </p>
      </motion.div>

      {/* Service type selector */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        data-testid="service-type-selector"
        className="mb-12 md:mb-16 grid grid-cols-3 gap-3 md:gap-4 max-w-4xl mx-auto"
      >
        {SERVICE_TYPES.map((type) => {
          const isActive = serviceType === type;
          const set = FORMULAS_SETS[type];
          return (
            <button
              key={type}
              type="button"
              onClick={() => setServiceType(type)}
              data-testid={`service-type-${type}`}
              className={`relative p-5 md:p-7 border transition-all group text-left ${
                isActive
                  ? "border-[#D90000] bg-[#D90000]/[0.06] shadow-lg shadow-[#D90000]/20"
                  : "border-white/10 bg-[#0a0a0a] hover:border-white/30"
              }`}
            >
              <div
                className={`text-[10px] uppercase tracking-[0.3em] font-bold mb-2 ${
                  isActive ? "text-[#D90000]" : "text-white/40"
                }`}
              >
                {isActive ? "★ Sélectionné" : "Nettoyage"}
              </div>
              <div className="font-impact text-2xl md:text-3xl lg:text-4xl leading-none mb-1">
                {set.label}
              </div>
              <div className={`text-xs md:text-sm ${isActive ? "text-white/80" : "text-white/50"}`}>
                {set.sublabel}
              </div>
              {isActive && (
                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-3 h-3 rotate-45 bg-[#D90000]" />
              )}
            </button>
          );
        })}
      </motion.div>

      <div className={gridClass}>
        {current.formulas.map((f, i) => (
          <motion.article
            key={`${serviceType}-${f.id}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`formula-${f.id}`}
            className={`relative flex flex-col p-8 md:p-10 border transition-all ${
              f.highlighted
                ? "border-[#D90000] bg-gradient-to-b from-[#1a0505] to-[#0a0a0a] shadow-2xl shadow-[#D90000]/25 md:scale-[1.05] md:-translate-y-2"
                : "border-white/10 bg-[#0a0a0a] hover:border-white/30"
            }`}
          >
            {f.highlighted && (
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#D90000] text-white text-[10px] uppercase tracking-[0.28em] font-bold px-5 py-2 whitespace-nowrap">
                ★ {f.ribbon}
              </div>
            )}

            <div className="text-[11px] uppercase tracking-[0.28em] text-[#D90000] font-semibold mb-3">
              Formule · {current.label}
            </div>
            <h3 className="font-impact text-5xl md:text-6xl leading-none mb-4">{f.name}</h3>
            <p className="text-white/70 text-sm mb-6 leading-relaxed min-h-[48px]">{f.summary}</p>

            <div className="text-[11px] uppercase tracking-[0.25em] text-white/50 mb-4">
              Durée : {f.duration}
            </div>

            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-white/50 text-sm">À partir de</span>
            </div>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="font-impact text-6xl md:text-7xl text-white leading-none">{f.price}</span>
              <span className="font-impact text-3xl text-[#D90000] leading-none">€</span>
              <span className="text-white/50 text-sm">TTC</span>
            </div>
            <div className="text-white/45 text-xs italic mb-8">(pour une citadine)</div>

            <ul className="space-y-3 mb-8 flex-1">
              {f.includes.map((it) => (
                <li key={it} className="flex items-start gap-3 text-sm text-white/85 leading-relaxed">
                  <Check size={16} className="text-[#D90000] mt-0.5 shrink-0" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>

            <a
              href="#contact"
              data-testid={`formula-cta-${f.id}`}
              className={`inline-flex items-center justify-center gap-3 w-full text-[12px] uppercase tracking-[0.22em] font-bold px-6 py-4 transition-all border ${
                f.highlighted
                  ? "bg-[#D90000] hover:bg-white hover:text-black text-white border-[#D90000] hover:border-white"
                  : "bg-transparent hover:bg-[#D90000] text-white border-white/25 hover:border-[#D90000]"
              }`}
            >
              Choisir cette formule <ArrowUpRight size={14} />
            </a>
          </motion.article>
        ))}
      </div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        data-testid="nett-supplements"
        className="mt-10 md:mt-14 border border-white/10 bg-[#0a0a0a] px-6 py-5 md:px-8 md:py-6 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8"
      >
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#D90000] font-bold whitespace-nowrap">
          Suppléments véhicule
        </div>
        <div className="hidden md:block w-px h-6 bg-white/15" />
        <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm text-white/75">
          <span><span className="text-white font-bold">Citadine</span>, tarif de base</span>
          <span><span className="text-white font-bold">Berline / Break</span> · <span className="text-[#D90000] font-bold">+10€</span></span>
          <span><span className="text-white font-bold">SUV / 4x4</span> · <span className="text-[#D90000] font-bold">+20€</span></span>
        </div>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        data-testid="nett-addons"
        className="mt-6 md:mt-8 border border-white/10 bg-[#0a0a0a] p-6 md:p-8"
      >
        <div className="flex items-center gap-3 mb-5">
          <Plus size={16} className="text-[#D90000]" />
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#D90000] font-bold">
            Options & prestations complémentaires
          </div>
        </div>
        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
          {ADDONS.map((a) => (
            <li
              key={a.label}
              data-testid={`addon-${a.label}`}
              className="flex items-center justify-between gap-4 py-2 border-b border-white/5 last:border-b-0 sm:last:border-b sm:[&:nth-last-child(-n+1)]:border-b-0"
            >
              <span className="text-sm text-white/85 leading-snug">{a.label}</span>
              <span className="font-impact text-lg text-[#D90000] whitespace-nowrap">{a.price}</span>
            </li>
          ))}
        </ul>
        <p className="text-white/45 text-xs mt-5 italic">
          Options cumulables avec toutes nos formules. Devis précis communiqué avant intervention.
        </p>
      </motion.div>
    </div>
  </section>
  );
};

/* ============ BENEFITS (client-focused) ============ */
const BenefitsSection = () => (
  <section data-testid="nett-benefits" className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a] relative overflow-hidden">
    {/* subtle red radial background */}
    <div className="pointer-events-none absolute -top-40 -right-40 w-[600px] h-[600px] bg-[#D90000]/8 blur-[120px] rounded-full" />
    <div className="pointer-events-none absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-[#D90000]/6 blur-[120px] rounded-full" />

    <div className="max-w-[1500px] mx-auto relative">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        className="max-w-3xl mb-16 md:mb-20"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-px bg-[#D90000]" />
          <span className="text-[11px] uppercase tracking-[0.35em] text-[#D90000]">— Ce que vous y gagnez</span>
        </div>
        <h2 className="font-impact text-5xl md:text-7xl lg:text-[90px] leading-[0.9]">
          BIEN PLUS QU'UN <br />
          <span className="text-[#D90000]">SIMPLE NETTOYAGE.</span>
        </h2>
        <p className="text-white/60 mt-8 text-base md:text-lg max-w-2xl leading-relaxed">
          Confier votre voiture à VistaWash, c'est offrir à votre véhicule un traitement à la hauteur de sa valeur, et retrouver, à chaque prestation, le plaisir de conduire un intérieur impeccable.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {BENEFITS.map((b, i) => {
          const Icon = b.icon;
          return (
            <motion.article
              key={b.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`benefit-${i}`}
              className="group relative p-8 md:p-10 border border-white/10 bg-black hover:border-[#D90000]/60 transition-all hover:-translate-y-1"
            >
              {/* Big red number in corner */}
              <span className="absolute top-4 right-6 font-impact text-6xl md:text-7xl text-white/[0.05] leading-none group-hover:text-[#D90000]/20 transition-colors">
                0{i + 1}
              </span>

              <div className="relative">
                <div className="w-14 h-14 flex items-center justify-center border border-[#D90000]/40 bg-[#D90000]/10 mb-6 group-hover:bg-[#D90000] transition-colors">
                  <Icon size={24} className="text-[#D90000] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-impact text-2xl md:text-3xl mb-4 leading-tight">{b.title}</h3>
                <p className="text-white/60 text-sm md:text-[15px] leading-relaxed">{b.text}</p>
              </div>
            </motion.article>
          );
        })}
      </div>

      {/* CTA band */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        data-testid="nett-benefits-cta"
        className="mt-14 md:mt-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 p-8 md:p-10 border border-white/10 bg-gradient-to-r from-[#1a0505] to-black"
      >
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#D90000] font-bold mb-3">
            Prêt à redécouvrir votre voiture ?
          </div>
          <div className="font-impact text-3xl md:text-4xl leading-tight">
            RÉSERVEZ VOTRE CRÉNEAU EN 30 SECONDES.
          </div>
        </div>
        <a
          href="#contact"
          data-testid="nett-benefits-cta-btn"
          className="inline-flex items-center gap-3 bg-[#D90000] hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-5 transition-colors whitespace-nowrap"
        >
          Demander mon devis <ArrowUpRight size={16} />
        </a>
      </motion.div>
    </div>
  </section>
);

/* ============ COMPARE ============ */
const CompareSection = () => (
  <section data-testid="nett-compare" className="py-24 md:py-32 px-6 md:px-12">
    <div className="max-w-[1500px] mx-auto">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Detailing vs Lavage classique</span>
        <h2 className="font-impact text-5xl md:text-6xl lg:text-[80px] leading-[0.9] mt-6">
          POURQUOI CHOISIR <br />
          UN <span className="text-[#D90000]">DETAILING</span> ?
        </h2>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-4 md:gap-6">
        {/* Colonne classique */}
        <div className="border border-white/10 bg-[#0a0a0a]">
          <div className="p-6 md:p-8 border-b border-white/10 flex items-center gap-4">
            <div className="w-10 h-10 bg-white/10 flex items-center justify-center">
              <X size={20} className="text-white/60" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-white/40">Standard</div>
              <div className="font-impact text-2xl md:text-3xl">Lavage classique</div>
            </div>
          </div>
          <div className="divide-y divide-white/5">
            {COMPARE.map((c) => (
              <div key={c.aspect} className="p-5 md:p-6">
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">{c.aspect}</div>
                <div className="text-white/60 text-sm md:text-base">{c.classic}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Colonne VistaWash */}
        <div className="border border-[#D90000] bg-gradient-to-b from-[#1a0505] to-[#0a0a0a] shadow-2xl shadow-[#D90000]/20">
          <div className="p-6 md:p-8 border-b border-[#D90000]/40 flex items-center gap-4">
            <div className="w-10 h-10 bg-[#D90000] flex items-center justify-center">
              <Check size={20} className="text-white" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#D90000] font-bold">Premium</div>
              <div className="font-impact text-2xl md:text-3xl">VistaWash Detailing</div>
            </div>
          </div>
          <div className="divide-y divide-[#D90000]/15">
            {COMPARE.map((c) => (
              <div key={c.aspect} className="p-5 md:p-6">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#D90000] mb-2">{c.aspect}</div>
                <div className="text-white text-sm md:text-base font-medium">{c.vistawash}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ============ REVIEWS ============ */
const ReviewsSection = () => (
  <section data-testid="nett-reviews" className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a]">
    <div className="max-w-[1500px] mx-auto">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-14 flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
        <div>
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Avis Google</span>
          <h2 className="font-impact text-5xl md:text-6xl lg:text-[80px] leading-[0.9] mt-6">
            ILS NOUS <br />
            <span className="text-[#D90000]">RECOMMANDENT</span>
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-0.5 text-[#FBBC04]">
            {[...Array(5)].map((_, j) => (
              <Star key={j} size={18} fill="currentColor" stroke="none" />
            ))}
          </div>
          <div>
            <div className="font-impact text-2xl text-white">5,0</div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-white/50">35 avis Google</div>
          </div>
        </div>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-4">
        {TESTIMONIALS.slice(0, 3).map((t, i) => (
          <motion.figure
            key={i}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={i}
            data-testid={`review-${i}`}
            className="p-7 border border-white/10 bg-[#050505] hover:border-[#D90000]/40 transition-colors flex flex-col gap-5"
          >
            <div className="flex items-center gap-3">
              {t.avatar ? (
                <img src={t.avatar} alt={t.name} className="w-11 h-11 rounded-full object-cover" />
              ) : (
                <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold" style={{ background: t.avatarBg }}>
                  {t.initial}
                </div>
              )}
              <div>
                <div className="text-white font-semibold text-sm">{t.name}</div>
                <div className="text-[11px] text-white/45">{t.meta}</div>
              </div>
            </div>
            <div className="flex gap-0.5 text-[#FBBC04]">
              {[...Array(t.rating)].map((_, j) => (
                <Star key={j} size={14} fill="currentColor" stroke="none" />
              ))}
            </div>
            <blockquote className="text-white/85 text-sm leading-relaxed">"{t.text}"</blockquote>
          </motion.figure>
        ))}
      </div>

      <div className="text-center mt-12">
        <a
          href="https://www.google.com/maps/place/VistaWash+-+Detailing/@50.4051,4.5256,17z/data=!4m8!3m7!1s0x47c237a9391aaedf:0x45cc5306c25c93ba!8m2!3d50.4051!4d4.5256!9m1!1b1"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-3 bg-[#D90000] hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-4 transition-all border border-[#D90000] hover:border-white"
        >
          Voir tous les avis Google <ArrowUpRight size={14} />
        </a>
      </div>
    </div>
  </section>
);

/* ============ FAQ ============ */
const FaqSection = () => {
  const [open, setOpen] = useState(0);
  return (
    <section data-testid="nett-faq" className="py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-[1500px] mx-auto grid md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— FAQ</span>
            <h2 className="font-impact text-4xl md:text-5xl lg:text-[64px] leading-[0.9] mt-6">
              QUESTIONS <br />
              <span className="text-[#D90000]">FRÉQUENTES</span>
            </h2>
          </motion.div>
        </div>
        <div className="md:col-span-8">
          <div className="divide-y divide-white/5">
            {FAQ.map((item, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setOpen(open === i ? -1 : i)}
                data-testid={`faq-${i}`}
                className="w-full text-left py-6 group"
              >
                <div className="flex items-start justify-between gap-6">
                  <h3 className="font-impact text-xl md:text-2xl leading-tight pr-4 uppercase tracking-wide">
                    {item.q}
                  </h3>
                  <div className="shrink-0 w-8 h-8 border border-white/20 group-hover:border-[#D90000] flex items-center justify-center text-white/60 group-hover:text-[#D90000] transition-colors">
                    {open === i ? <Minus size={14} /> : <Plus size={14} />}
                  </div>
                </div>
                <AnimatePresence>
                  {open === i && (
                    <motion.p
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      className="text-white/65 leading-relaxed mt-4 max-w-3xl overflow-hidden"
                    >
                      {item.a}
                    </motion.p>
                  )}
                </AnimatePresence>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============ FINAL CTA + FORM ============ */
const FinalCTA = () => (
  <section id="contact" data-testid="nett-final-cta" className="py-24 md:py-32 px-6 md:px-12 bg-gradient-to-b from-[#1a0505] to-black border-t border-[#D90000]/20">
    <div className="max-w-[1500px] mx-auto grid md:grid-cols-12 gap-12">
      <div className="md:col-span-5">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Passez à l'action</span>
        <h2 className="font-impact text-5xl md:text-6xl lg:text-[84px] leading-[0.9] mt-6 mb-8">
          VOTRE VÉHICULE <br />
          <span className="text-[#D90000]">MÉRITE MIEUX</span> <br />
          QU'UN LAVAGE.
        </h2>
        <p className="text-white/75 leading-relaxed max-w-md mb-10">
          Devis gratuit et personnalisé. Réponse rapide par WhatsApp, lun–sam 9h–18h. Notre expertise à votre service pour redonner à votre voiture toute sa valeur.
        </p>
        <div className="flex flex-wrap gap-4 mb-10">
          <a
            href="#contact-form"
            data-testid="final-devis-cta"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('[data-testid="quote-form"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
            }}
            className="inline-flex items-center gap-3 bg-[#D90000] hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-5 transition-all border border-[#D90000] hover:border-white"
          >
            Recevoir mon devis gratuit <ArrowUpRight size={16} />
          </a>
          <a
            href={COMPANY.phone_link}
            data-testid="final-phone-cta"
            className="inline-flex items-center gap-3 border-2 border-white/30 hover:border-white hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-5 transition-all"
          >
            <Phone size={16} /> {COMPANY.phone_display}
          </a>
        </div>
        <div className="grid grid-cols-2 gap-6 pt-8 border-t border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#D90000] mb-2">Atelier</div>
            <div className="text-white text-sm">{COMPANY.address_line1}</div>
            <div className="text-white/70 text-sm">{COMPANY.address_line2}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#D90000] mb-2">Email</div>
            <a href={`mailto:${COMPANY.email}`} className="text-white text-sm hover:text-[#D90000] transition-colors">
              {COMPANY.email}
            </a>
          </div>
        </div>
      </div>

      <div className="md:col-span-7 bg-black/40 border border-white/10 p-8 md:p-10">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#D90000] mb-2">— Formulaire de devis</div>
        <h3 className="font-impact text-3xl md:text-4xl mb-8 leading-tight">
          Décrivez-nous votre besoin
        </h3>
        <QuoteForm defaultService="Nettoyage intérieur / extérieur" />
      </div>
    </div>
  </section>
);

export default NettoyagePremium;
